import { type SupportBlocking, type SupportLine, type SupportStatus } from '@onr/schema'

import { type SupportRequestAgentDocument } from './types'

export type AgentQueueFilter = 'toHandle' | 'waiting' | 'valian' | 'open' | 'settled'
export type AgentQueueEscouade = 'all' | 'me' | 'none'

export interface AgentQueueCriteria {
  readonly filter: AgentQueueFilter
  readonly escouade: AgentQueueEscouade
  readonly query: string
  readonly searched: SupportRequestAgentDocument | undefined
  readonly me: number | null
}

export const AGENT_QUEUE_FILTERS = {
  l1: ['toHandle', 'waiting', 'settled'],
  l2: ['valian', 'open', 'settled'],
} as const satisfies Record<SupportLine, ReadonlyArray<AgentQueueFilter>>

export const agentQueueFilterOf = (line: SupportLine, requested?: AgentQueueFilter): AgentQueueFilter => {
  const filters: ReadonlyArray<AgentQueueFilter> = AGENT_QUEUE_FILTERS[line]
  return requested && filters.includes(requested) ? requested : filters[0]
}

const BLOCKING_RANK = { blocked: 0, slowed: 1, canContinue: 2 } as const satisfies Record<SupportBlocking, number>
const SEARCHED_NUMBER = /^#?\s*(\d+)$/

export const searchedNumberOf = (query: string) => {
  const digits = SEARCHED_NUMBER.exec(query.trim())?.[1]
  return digits === undefined ? null : Number(digits)
}

export const matchesQueueFilter = (filter: AgentQueueFilter, status: SupportStatus) => {
  switch (filter) {
    case 'toHandle':
      return status === 'toHandle'
    case 'waiting':
      return status === 'waitingRequester' || status === 'waitingValian'
    case 'valian':
      return status === 'waitingValian'
    case 'open':
      return status !== 'settled'
    case 'settled':
      return status === 'settled'
  }
}

// The line's first filter listing a request of that status, so a direct link shows the request.
export const agentQueueFilterFor = (line: SupportLine, status: SupportStatus): AgentQueueFilter =>
  AGENT_QUEUE_FILTERS[line].find((filter) => matchesQueueFilter(filter, status)) ?? AGENT_QUEUE_FILTERS[line][0]

const matchesEscouade = (escouade: AgentQueueEscouade, me: number | null, request: SupportRequestAgentDocument) => {
  switch (escouade) {
    case 'all':
      return true
    case 'me':
      return request.assignee !== null && request.assignee.piloteUserId === me
    case 'none':
      return request.assignee === null
  }
}

const byBlockingThenOldest = (a: SupportRequestAgentDocument, b: SupportRequestAgentDocument) =>
  BLOCKING_RANK[a.blocking] - BLOCKING_RANK[b.blocking] || a.statusSince.getTime() - b.statusSince.getTime()

// A search spans every listed request whatever the status filter, and an exact number also reaches
// a request settled before the retention window through its direct read.
export const selectAgentQueue = (
  requests: ReadonlyArray<SupportRequestAgentDocument>,
  { filter, escouade, query, searched, me }: AgentQueueCriteria
) => {
  const term = query.trim().toLocaleLowerCase()
  const number = searchedNumberOf(query)
  const matching =
    term === ''
      ? requests.filter((request) => matchesQueueFilter(filter, request.status))
      : [
          ...(searched && !requests.some((request) => request.number === searched.number) ? [searched] : []),
          ...requests.filter(
            (request) =>
              request.number === number ||
              request.title.toLocaleLowerCase().includes(term) ||
              request.centralName.toLocaleLowerCase().includes(term)
          ),
        ]
  return matching.filter((request) => matchesEscouade(escouade, me, request)).sort(byBlockingThenOldest)
}

export const countsTowardAgentBadge = (line: SupportLine, status: SupportStatus) =>
  line === 'l2' ? status === 'waitingValian' : status !== 'settled'
